'use client';

import { Link } from '@/components/ui/Link';
import { SaveAccountAction } from '@/features/account/actions/saveAccount';
import { useTranslation } from '@/features/i18n';
import { ApplicationLogo } from '@/ui/images/ApplicationLogo';
import { logger } from '@/utils/loggerUtils';
import {
  appendReturnUrl,
  getReturnUrlFromParams,
  storeRedirectUrl,
} from '@/utils/redirectUtils';
import { useAuthentication } from '@vimond/features/authentication';
import { authenticateUserInRestAPI } from '@vimond/services/rest-api';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { VippsSignInButton } from '../buttons/VippsSignInButton';
import { SignUpConfirmCodeForm } from './SignUpConfirmCodeForm';
import { SignUpPasswordForm } from './SignUpPasswordForm';

const CREATE_PASSWORD = 'CREATE_PASSWORD';
const CONFIRM_CODE = 'CONFIRM_CODE';

type Step = 'CREATE_PASSWORD' | 'CONFIRM_CODE';

export const SignUpForm = () => {
  const { signIn } = useAuthentication();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<Step>(CREATE_PASSWORD);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = getReturnUrlFromParams(searchParams?.toString());

  // Store returnUrl in sessionStorage so it persists through the auth flow
  useEffect(() => {
    if (returnUrl) {
      storeRedirectUrl(returnUrl);
    }
  }, [returnUrl]);

  const logIn = async (_email = email, _password = password): Promise<void> => {
    try {
      const res = await signIn({ email: _email, password: _password });
      await SaveAccountAction({
        data: { email: _email, preferredUsername: _email },
      });
      if (res.isSignedIn) {
        // redirect to who is watching page, preserving returnUrl
        await authenticateUserInRestAPI();
        const profilesUrl = returnUrl
          ? appendReturnUrl('/profiles/whoiswatching', returnUrl)
          : '/profiles/whoiswatching';
        router.push(profilesUrl);
      } else {
        router.push('/auth/signin');
      }
    } catch (err) {
      logger.error(err);
      router.push('/auth/signin');
    }
  };

  return (
    <section>
      <div className="mx-auto flex flex-col items-center justify-center px-6 py-8 lg:h-screen lg:py-0">
        <ApplicationLogo />
        <div className="border-background-border bg-background-secondary w-full rounded-lg border shadow sm:max-w-md md:mt-0 xl:p-0">
          <div className="space-y-4 p-6 sm:p-8 md:space-y-6">
            <h1 className="text-center text-xl font-bold leading-tight tracking-tight md:text-2xl">
              {t('auth.sign-up-for-an-account')}
            </h1>
            <VippsSignInButton onSignInError={setErrorMessage} />
            {errorMessage && (
              <p className="text-red-500 text-center py-2">{errorMessage}</p>
            )}

            {step === CREATE_PASSWORD && (
              <SignUpPasswordForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                confirmCode={() => setStep(CONFIRM_CODE)}
                logIn={logIn}
              />
            )}
            {step === CONFIRM_CODE && (
              <SignUpConfirmCodeForm email={email} logIn={logIn} />
            )}
            <p className="text-text-secondary mt-10 text-center text-sm">
              {t('auth.have-an-account')}{' '}
              <Link
                href="/auth/signin"
                className="text-text-highlight font-semibold leading-6"
              >
                {t('auth.sign-in-now')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
